import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface AlertHistoryRow {
  _id: string;
  status: string;
  rule_signature?: string;
  rule?: { name?: string; subject?: string; condition?: string; target?: string };
  rulegroup?: { name?: string };
  opened_at?: string;
  acknowledged_at?: string;
  acknowledged_by?: { firstname?: string; lastname?: string } | string;
  closed_at?: string;
}

// The datatable endpoint returns rows as arrays, not objects. We use the
// raw Mongo query via GET /v2/alerthistories instead.
export const alertHistoryKeys = {
  all: ['sensor-alert-history'] as const,
  list: (id: string) => [...alertHistoryKeys.all, id] as const,
};

export function useSensorAlertHistory(mongoId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: alertHistoryKeys.list(mongoId ?? ''),
    queryFn: async (): Promise<AlertHistoryRow[]> => {
      if (!mongoId) return [];
      const where = JSON.stringify({ sensor: mongoId });
      const options = JSON.stringify({
        sort: { opened_at: -1 },
        limit: 30,
        populate: [
          { path: 'acknowledged_by', select: 'firstname lastname' },
          { path: 'rulegroup', select: 'name rules' },
        ],
      });
      const rows = await api.get<AlertHistoryRow[]>(`/v2/alerthistories`, {
        where,
        options,
      });
      return Array.isArray(rows) ? rows : [];
    },
    enabled: enabled && !!mongoId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
